import {
	Account,
	AdvisorInquiries,
	AdvisorInquiriesDashboard,
	AdvisorInquiriesDetailEdit,
	AdvisoryTeam,
	AdvisoryTeamDetail,
	AskHackett,
	BestPracticeDetail,
	BestPractices,
	BlogPostDetail,
	BlogPostDisplayPublic,
	BlogPosts,
	BlogPostsPublic,
	Bookmarks,
	BpoVendorEnablement,
	ClientInquiries,
	CompleteProfile,
	Contact,
	ContentNoLongerExists,
	CreateInquiry,
	DashboardV2,
	Deliverables,
	DeliverablesCreate,
	Engagement,
	Events,
	EventStreamTest,
	InquiryDetailReadOnly,
	InquiryDocumentDetail,
	InquiryMarketing,
	Maintenance,
	NoMatch,
	PerformanceMetrics,
	PerformanceMetricsDetail,
	ProcurementVendorEnablement,
	ProcurementTechnologyIntelligence,
	ProgramBundle,
	ReportAiConversation,
	ReportDetail,
	Reports,
	Research,
	ResearchDetail,
	Search,
	SignIn,
	SolutionIntelligence,
	ToolLaunch,
	ToolDetail,
	Tools,
	UseCaseDetail,
	UseCases,
	VendorEnablement,
	Version,
} from '@/pages';

import { HeaderNoAuth, Header, HeaderStandalone, Footer } from '@/components';
import { Navigate, RouteProps, useLocation, useParams } from 'react-router-dom';

type RouteConfig = {
	private: boolean;
	header: React.ComponentType;
	main: React.ComponentType;
	footer: React.ComponentType;
} & RouteProps;

const BlogRouteRedirect = () => {
	const { documentId } = useParams<{ documentId?: string }>();
	const { search } = useLocation();

	if (!documentId) {
		return <Navigate to={`/research`} replace />;
	}

	return <Navigate to={`/research/${documentId}${search}`} replace />;
};

export const routes: RouteConfig[] = [
	{
		path: '/sign-in',
		private: false,
		header: () => <HeaderNoAuth />,
		main: SignIn,
		footer: Footer,
	},
	{
		path: '/complete-profile',
		private: true,
		header: () => <HeaderNoAuth />,
		main: CompleteProfile,
		footer: Footer,
	},
	{
		path: '/',
		private: true,
		header: Header,
		main: DashboardV2,
		footer: Footer,
	},
	{
		path: '/search',
		private: true,
		header: Header,
		main: Search,
		footer: Footer,
	},
	{
		path: '/search/:query',
		private: true,
		header: Header,
		main: Search,
		footer: Footer,
	},
	{
		path: '/program-bundle/solution-intelligence',
		private: true,
		header: Header,
		main: SolutionIntelligence,
		footer: Footer,
	},
	{
		path: '/program-bundle/market-intelligence',
		private: true,
		header: Header,
		main: () => <Navigate to="/program-bundle/solution-intelligence" replace />,
		footer: Footer,
	},
	{
		path: '/program-bundle/pr-tech-intelligence',
		private: true,
		header: Header,
		main: ProcurementTechnologyIntelligence,
		footer: Footer,
	},
	{
		path: '/program-bundle/vendor-enablement',
		private: true,
		header: Header,
		main: VendorEnablement,
		footer: Footer,
	},
	{
		path: '/program-bundle/bpo-vendor-enablement-program',
		private: true,
		header: Header,
		main: BpoVendorEnablement,
		footer: Footer,
	},
	{
		path: '/program-bundle/pr-vendor-enablement',
		private: true,
		header: Header,
		main: ProcurementVendorEnablement,
		footer: Footer,
	},
	{
		path: '/program-bundle/:bundleId',
		private: true,
		header: Header,
		main: ProgramBundle,
		footer: Footer,
	},
	{
		path: '/research',
		private: true,
		header: Header,
		main: Research,
		footer: Footer,
	},
	{
		path: '/research/:documentId',
		private: true,
		header: Header,
		main: ResearchDetail,
		footer: Footer,
	},
	{
		path: '/blog/:documentId',
		private: true,
		header: Header,
		main: BlogRouteRedirect,
		footer: Footer,
	},
	{
		path: '/blogs/:documentId',
		private: true,
		header: Header,
		main: BlogRouteRedirect,
		footer: Footer,
	},
	{
		path: '/best-practices',
		private: true,
		header: Header,
		main: BestPractices,
		footer: Footer,
	},
	{
		path: '/best-practices/:bestPracticeId',
		private: true,
		header: Header,
		main: BestPracticeDetail,
		footer: Footer,
	},
	{
		path: '/performance-metrics',
		private: true,
		header: Header,
		main: PerformanceMetrics,
		footer: Footer,
	},
	{
		path: '/performance-metrics/:performanceMetricId',
		private: true,
		header: Header,
		main: PerformanceMetricsDetail,
		footer: Footer,
	},
	{
		path: '/engagement/*',
		private: true,
		header: Header,
		main: Engagement,
		footer: Footer,
	},
	{
		path: '/events/*',
		private: true,
		header: Header,
		main: Events,
		footer: Footer,
	},
	{
		path: '/client-management',
		private: true,
		header: Header,
		main: () => <Navigate to="/client-management/inquiries-dashboard" replace />,
		footer: Footer,
	},
	{
		path: '/client-management/inquiries-dashboard',
		private: true,
		header: Header,
		main: AdvisorInquiriesDashboard,
		footer: Footer,
	},
	{
		path: '/client-management/inquiries',
		private: true,
		header: Header,
		main: AdvisorInquiries,
		footer: Footer,
	},
	{
		path: '/client-management/inquiries/:inquiryId',
		private: true,
		header: Header,
		main: InquiryDetailReadOnly,
		footer: Footer,
	},
	{
		path: '/client-management/inquiries/:inquiryId/edit',
		private: true,
		header: Header,
		main: AdvisorInquiriesDetailEdit,
		footer: Footer,
	},
	{
		path: '/client-management/deliverables',
		private: true,
		header: Header,
		main: Deliverables,
		footer: Footer,
	},
	{
		path: '/client-management/deliverables/create',
		private: true,
		header: Header,
		main: DeliverablesCreate,
		footer: Footer,
	},
	{
		path: '/client-management/deliverables/:deliverableId',
		private: true,
		header: Header,
		main: DeliverablesCreate,
		footer: Footer,
	},
	{
		path: '/client-management/reports',
		private: true,
		header: Header,
		main: Reports,
		footer: Footer,
	},
	{
		path: '/client-management/reports/:reportId',
		private: true,
		header: Header,
		main: ReportDetail,
		footer: Footer,
	},
	{
		path: '/ask-hackett',
		private: true,
		header: HeaderStandalone,
		main: AskHackett,
		footer: () => null,
	},
	{
		path: '/tools',
		private: true,
		header: Header,
		main: Tools,
		footer: Footer,
	},
	{
		path: '/tools/:toolId/versions/:toolVersionId/preview/launch',
		private: true,
		header: () => <HeaderStandalone backTo="/tools" />,
		main: ToolLaunch,
		footer: () => null,
	},
	{
		path: '/tools/:toolId/launch',
		private: true,
		header: () => <HeaderStandalone backTo="/tools" />,
		main: ToolLaunch,
		footer: () => null,
	},
	{
		path: '/tools/:toolId/versions/:toolVersionId/preview',
		private: true,
		header: Header,
		main: ToolDetail,
		footer: Footer,
	},
	{
		path: '/tools/:toolId',
		private: true,
		header: Header,
		main: ToolDetail,
		footer: Footer,
	},
	{
		path: '/report/ai-conversation/:aiConversationId',
		private: true,
		header: HeaderStandalone,
		main: ReportAiConversation,
		footer: () => null,
	},
	{
		path: '/inquiries',
		private: true,
		header: Header,
		main: ClientInquiries,
		footer: Footer,
	},
	{
		path: '/inquiries/learn-more',
		private: true,
		header: Header,
		main: InquiryMarketing,
		footer: Footer,
	},
	{
		path: '/inquiries/:inquiryId',
		private: true,
		header: Header,
		main: InquiryDetailReadOnly,
		footer: Footer,
	},
	{
		path: '/inquiries/:inquiryId/document/:documentId',
		private: true,
		header: Header,
		main: InquiryDocumentDetail,
		footer: Footer,
	},
	{
		path: '/contact',
		private: true,
		header: Header,
		main: Contact,
		footer: Footer,
	},
	{
		path: '/contact/create-inquiry',
		private: true,
		header: Header,
		main: CreateInquiry,
		footer: Footer,
	},
	{
		path: '/advisory-team',
		private: true,
		header: Header,
		main: AdvisoryTeam,
		footer: Footer,
	},
	{
		path: '/advisory-team/:advisorId',
		private: true,
		header: Header,
		main: AdvisoryTeamDetail,
		footer: Footer,
	},
	{
		path: '/account/*',
		private: true,
		header: Header,
		main: Account,
		footer: Footer,
	},
	{
		path: '/bookmarks',
		private: true,
		header: Header,
		main: Bookmarks,
		footer: Footer,
	},
	{
		path: '/public/blogs',
		private: false,
		header: () => <HeaderNoAuth showSignInButton />,
		main: BlogPostsPublic,
		footer: Footer,
	},
	{
		path: '/public/blogs/:blogId',
		private: false,
		header: () => <HeaderNoAuth showSignInButton />,
		main: BlogPostDisplayPublic,
		footer: Footer,
	},
	{
		path: '/blog-posts',
		private: true,
		header: Header,
		main: BlogPosts,
		footer: Footer,
	},
	{
		path: '/blog-posts/create',
		private: true,
		header: Header,
		main: BlogPostDetail,
		footer: Footer,
	},
	{
		path: '/blog-posts/:blogId',
		private: true,
		header: Header,
		main: BlogPostDetail,
		footer: Footer,
	},
	{
		path: '/use-cases',
		private: true,
		header: Header,
		main: UseCases,
		footer: Footer,
	},
	{
		path: '/use-cases/:useCaseId',
		private: true,
		header: Header,
		main: UseCaseDetail,
		footer: Footer,
	},
	{
		path: '/event-stream-test',
		private: true,
		header: HeaderStandalone,
		main: EventStreamTest,
		footer: () => null,
	},
	{
		path: '/version',
		private: true,
		header: Header,
		main: Version,
		footer: Footer,
	},
	{
		path: '/maintenance',
		private: false,
		header: () => <HeaderNoAuth />,
		main: Maintenance,
		footer: Footer,
	},
	{
		path: '/content-no-longer-exists',
		private: true,
		header: Header,
		main: ContentNoLongerExists,
		footer: Footer,
	},
	{
		path: '/public/content-no-longer-exists',
		private: false,
		header: () => <HeaderNoAuth showSignInButton />,
		main: ContentNoLongerExists,
		footer: Footer,
	},
	{
		path: '*',
		private: false,
		header: Header,
		main: NoMatch,
		footer: Footer,
	},
];
